import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DESIGN_REQUESTS } from "@/constants/designRequestTestIds";

const STATUS_COPY = {
  draft:      { label: "Draft",       color: "#6B6358" },
  generating: { label: "Generating…", color: "#B5462F" },
  ready:      { label: "Ready",       color: "#1F6B6B" },
  failed:     { label: "Failed",      color: "#B5462F" },
};

export default function DesignRequests() {
  const { brandId } = useParams();
  const { user } = useAuth();
  const isAdmin = !!user?.is_platform_admin;
  const [brand, setBrand] = useState(null);
  const [requests, setRequests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [contentItems, setContentItems] = useState([]);
  const [catalog, setCatalog] = useState({ campaigns: [], promotions_by_campaign: {}, event_types: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    template_id: "", program_id: "", location_id: "",
    campaign: "", promotion: "", event_type: "",
    campaign_brief: "", content_item_ids: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    const [b, r, t, prog, l, promos, cruises, cat] = await Promise.all([
      api.get(`/brands/${brandId}`),
      api.get(`/brands/${brandId}/design-requests`),
      api.get(`/templates`, { params: { brand_id: brandId } }),
      api.get(`/brands/${brandId}/programs`).catch(() => ({ data: [] })),
      api.get(`/brands/${brandId}/my-locations`),
      api.get(`/brands/${brandId}/content-categories/cat_demo_promotion/items`).catch(() => ({ data: [] })),
      api.get(`/brands/${brandId}/content-categories/cat_demo_cruise_product/items`).catch(() => ({ data: [] })),
      api.get(`/brands/${brandId}/campaign-catalog`).catch(() => ({ data: { campaigns: [], promotions_by_campaign: {}, event_types: [] } })),
    ]);
    setBrand(b.data);
    setRequests(r.data);
    setTemplates(t.data);
    const activePrograms = (prog.data || []).filter((p) => p.lifecycle_status !== "retired");
    setPrograms(activePrograms);
    // Locations are sorted alphabetically so the form can default to the
    // first-alphabetical entry (the user's "anchored location" concept
    // will eventually replace this default — see PRD).
    const sortedLocations = [...l.data].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
    setLocations(sortedLocations);
    setContentItems([...(promos.data || []), ...(cruises.data || [])]);
    setCatalog(cat.data || { campaigns: [], promotions_by_campaign: {}, event_types: [] });
    setForm((prev) => ({
      ...prev,
      template_id: prev.template_id || t.data[0]?.template_id || "",
      program_id: prev.program_id || activePrograms[0]?.program_id || "",
      location_id: prev.location_id || sortedLocations[0]?.location_id || "",
    }));
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [brandId]);

  // Phase 4 Policies: a program is selectable only when its eligible_location_ids
  // is empty (all locations) or includes the chosen location. Admins bypass.
  const programEligible = (p) => {
    if (isAdmin) return true;
    const elig = p.policies?.eligible_location_ids || [];
    if (elig.length === 0) return true;
    return !!form.location_id && elig.includes(form.location_id);
  };
  const eligiblePrograms = programs.filter(programEligible);

  // Keep the selected program valid as the location changes.
  useEffect(() => {
    if (!programs.length) return;
    if (!eligiblePrograms.some((p) => p.program_id === form.program_id)) {
      setForm((prev) => ({ ...prev, program_id: eligiblePrograms[0]?.program_id || "" }));
    }
    // eslint-disable-next-line
  }, [form.location_id, programs]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post(`/brands/${brandId}/design-requests`, form);
      setShowForm(false);
      setForm({
        template_id: templates[0]?.template_id || "",
        program_id: programs[0]?.program_id || "",
        location_id: locations[0]?.location_id || "",
        campaign: "", promotion: "", event_type: "",
        campaign_brief: "", content_item_ids: [],
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create");
    } finally { setSubmitting(false); }
  };

  const toggleContentItem = (id) => {
    setForm((prev) => {
      const next = new Set(prev.content_item_ids);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...prev, content_item_ids: Array.from(next) };
    });
  };

  if (loading) return <p className="text-[#6B6358]">Loading design requests…</p>;
  if (!brand) return <p className="text-[#B5462F]">Brand not found</p>;

  return (
    <div data-testid={DESIGN_REQUESTS.listRoot} className="max-w-6xl">
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">
        {brand.name} &middot; Studio
      </p>
      <h1 className="font-serif text-5xl tracking-tight">Studio</h1>
      <p className="text-base text-[#3D3A35] mt-3 max-w-2xl">
        Ask the AI Assembly Agent for a six-panel wrap. It pulls from this brand&rsquo;s
        Brand Kit, Content Library, location attributes, and active rules — applying
        the <em>filter → priority → tiebreak</em> policy for each binding.
      </p>

      <div className="flex items-baseline justify-between mt-10 mb-5">
        <h2 className="font-serif text-2xl">Projects</h2>
        {!showForm && (
          <button
            data-testid={DESIGN_REQUESTS.newButton}
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm"
          >
            + New project
          </button>
        )}
      </div>

      {showForm && (
        <form
          data-testid={DESIGN_REQUESTS.formRoot}
          onSubmit={submit}
          className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-8"
        >
          <h3 className="font-serif text-lg mb-4">New project</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">Brand Program</label>
              <select
                data-testid="dreq-form-program"
                value={form.program_id}
                onChange={(e) => setForm({ ...form, program_id: e.target.value })}
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
              >
                {eligiblePrograms.length === 0 ? (
                  <option value="">{programs.length === 0 ? "No brand programs yet" : "No programs for this location"}</option>
                ) : (
                  eligiblePrograms.map((p) => (
                    <option key={p.program_id} value={p.program_id}>
                      {`${p.name} · ${p.program_type}`}
                    </option>
                  ))
                )}
              </select>
              {eligiblePrograms.length === 0 && (
                <p className="text-xs text-[#6B6358] mt-1">
                  {programs.length === 0
                    ? "Create a Brand Program to apply its content & production rules to this project."
                    : "No program is available for the selected location. Pick another location or adjust the program's policies."}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">Location</label>
              <select
                data-testid={DESIGN_REQUESTS.formLocation}
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
              >
                {locations.map((l) => (
                  <option key={l.location_id} value={l.location_id}>
                    {`${l.name} · ${l.location_code || ""}`}
                  </option>
                ))}
                <option value="">— brand-wide —</option>
              </select>
            </div>

            <div className="pt-2 mt-2 border-t border-[#1A1F2C]/10">
              <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">Content Selections</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#6B6358] mb-1">Campaign</label>
                  <select
                    data-testid="dreq-form-campaign"
                    value={form.campaign}
                    onChange={(e) => setForm({ ...form, campaign: e.target.value, promotion: "" })}
                    className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
                  >
                    <option value="">Choose Campaign</option>
                    {(catalog.campaigns || []).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#6B6358] mb-1">Promotion</label>
                  <select
                    data-testid="dreq-form-promotion"
                    value={form.promotion}
                    onChange={(e) => setForm({ ...form, promotion: e.target.value })}
                    disabled={!form.campaign}
                    className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] disabled:opacity-50"
                  >
                    <option value="">{form.campaign ? "Choose Promotion" : "Pick a Campaign first"}</option>
                    {(catalog.promotions_by_campaign?.[form.campaign] || []).map((p) => {
                      // Two shapes: static catalog → string; brand-curated
                      // catalog → { item_id, name }. We always use item_id
                      // as the value when present so the dreq creation
                      // endpoint can resolve to the actual Content Library
                      // item.
                      const isObj = typeof p === "object" && p !== null;
                      const val = isObj ? p.item_id : p;
                      const label = isObj ? p.name : p;
                      return <option key={val} value={val}>{label}</option>;
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#6B6358] mb-1">Event</label>
                  <select
                    data-testid="dreq-form-event"
                    value={form.event_type}
                    onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                    className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
                  >
                    <option value="">Choose Event</option>
                    {(catalog.event_types || []).map((ev) => (
                      <option key={ev} value={ev}>{ev}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-[10px] uppercase tracking-[0.15em] text-[#6B6358] mb-1">Additional notes</label>
                <textarea
                  data-testid={DESIGN_REQUESTS.formBrief}
                  rows={4}
                  value={form.campaign_brief}
                  onChange={(e) => setForm({ ...form, campaign_brief: e.target.value })}
                  placeholder="Anything else the AI Assembly agent should know — must-include taglines, sail dates, vibe, audience emphasis. The agent can edit brand-supplied copy to fit available space."
                  className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-2">
                Content items to draw from (optional)
              </label>
              {contentItems.length === 0 ? (
                <p className="text-xs text-[#6B6358]">No content items in this brand yet. The AI will rely on the brief alone.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {contentItems.map((it) => {
                    const active = form.content_item_ids.includes(it.item_id);
                    return (
                      <button
                        type="button"
                        key={it.item_id}
                        data-testid={`${DESIGN_REQUESTS.formContentItemPrefix}${it.item_id}`}
                        onClick={() => toggleContentItem(it.item_id)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          active
                            ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]"
                            : "border-[#1A1F2C]/20 text-[#3D3A35] hover:border-[#1A1F2C]/40"
                        }`}
                      >
                        {it.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <div className="flex gap-3 mt-5">
            <button
              type="submit"
              data-testid={DESIGN_REQUESTS.formSubmit}
              disabled={submitting}
              className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium disabled:opacity-60"
            >
              {submitting ? "Creating…" : "Create request"}
            </button>
            <button
              type="button"
              data-testid={DESIGN_REQUESTS.formCancel}
              onClick={() => setShowForm(false)}
              className="px-5 py-2 text-sm text-[#6B6358]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {requests.length === 0 ? (
        <p data-testid={DESIGN_REQUESTS.emptyState} className="text-sm text-[#6B6358] border border-dashed border-[#1A1F2C]/15 rounded-lg px-6 py-10 text-center">
          No projects yet. Click "New project" to author one.
        </p>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => {
            const status = STATUS_COPY[r.status] || { label: r.status, color: "#3D3A35" };
            return (
              <Link
                key={r.design_request_id}
                to={`/brands/${brandId}/design-requests/${r.design_request_id}`}
                data-testid={`${DESIGN_REQUESTS.rowPrefix}${r.design_request_id}`}
                className="block border border-[#1A1F2C]/10 bg-white rounded-lg px-5 py-4 hover:border-[#1A1F2C]/30 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-base font-medium text-[#1A1F2C]">
                      {r.name || r.promotion || r.campaign || "Untitled draft"}
                    </h3>
                    <div className="text-[10px] font-mono text-[#6B6358] mt-0.5">{r.design_request_id}</div>
                    {r.campaign_brief && (
                      <p className="text-xs text-[#3D3A35] mt-1 line-clamp-1 italic">&ldquo;{r.campaign_brief}&rdquo;</p>
                    )}
                  </div>
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-0.5 rounded-full shrink-0"
                    style={{ backgroundColor: `${status.color}1a`, color: status.color }}
                  >
                    {status.label}
                  </span>
                </div>
                {r.assembly_summary && (
                  <div className="text-xs text-[#6B6358] mt-2">
                    {r.assembly_summary.elements} elements · {r.assembly_summary.warnings} warnings · {r.assembly_summary.missing_assets} missing
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
